/**
 * E-GTCC-VIEW-TEN1 — "Tenants & Access", the M3 ADMINISTRATION view that
 * merges the old console's tenant list, provisioning, Tenant 360°, members,
 * invitations, role catalog and domain-routing readiness into one place.
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:680-736` (head, `.sub`, the
 * `.tab`s at `font-size:12px`, the tenants table, `.grid-main`). The reference
 * head is an `h1`; inside GTCC the shell owns the page `h1`
 * (`GtccShell.tsx:186`), so the view head is an `h2` carrying the reference's
 * head type scale — the same deviation the cockpit and the data plane already
 * made (`GtccCockpitView.tsx:981`, `DplaneView.tsx:154`).
 *
 * One deliberate deviation from the reference markup remains, from the Owner
 * order (P19): the reference `.sub` ends in "Beispieldaten." — this view
 * reads live admin endpoints, so that sentence is gone.
 *
 * B-GTCC-FIX-TEN1 (`ABWEICHUNGEN.md` §E "ten") undid the other two
 * deviations E-GTCC-VIEW-TEN1 shipped: the tab bar now draws all six
 * reference tabs ("Einladungen" and "Rollen" separately, see
 * `InvitationsTab.tsx`/`RolesTab.tsx`), and the extra read-only footnote
 * below the panels is gone — the reference carries no such sentence.
 *
 * Registry keys `ten`, `dom` and the old tenant-page keys resolve here; the
 * old views stay on disk until D-GTCC-SHELL1 merges.
 *
 * Read paths only. Every write this view offers belongs to a gtcc1 card that
 * is framed here unchanged (lifecycle verbs, member role/status/removal,
 * device management, tenant creation with first-admin invitation). No new
 * write path is introduced.
 */
import { Stack, Tabs, type TabItem } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from "react";

import { fetchTenLaunchStatus, fetchTenTenants, TEN_TENANTS_KEY } from "../../api/ten.js";
import { FleetFreshBadge, NotMeasurable } from "../cockpit/components/FleetPrimitives.js";
import {
  SUB_STYLE,
  TAB_BAR_STYLE,
  TEN_QUERY_OPTIONS,
  TEN_TAB_IDS,
  type TenTabId,
} from "./components/TenPanels.js";
import { InvitationsTab } from "./tabs/InvitationsTab.js";
import { MembersTab } from "./tabs/MembersTab.js";
import { ProvisioningTab } from "./tabs/ProvisioningTab.js";
import { RolesTab } from "./tabs/RolesTab.js";
import { Tenant360Tab } from "./tabs/Tenant360Tab.js";
import { TenantsTab } from "./tabs/TenantsTab.js";

/* ------------------------------------------------------------------ tabs */

const DEFAULT_TEN_TAB: TenTabId = "tenants";
const TAB_ID_PREFIX = "gtcc-ten";

function isTenTabId(value: string): value is TenTabId {
  return (TEN_TAB_IDS as ReadonlyArray<string>).includes(value);
}

/** Mirrors `initialTabFromQuery()` in `DplaneView.tsx:50`. */
function initialTabFromQuery(): TenTabId {
  if (typeof window === "undefined") return DEFAULT_TEN_TAB;
  const requested = new URLSearchParams(window.location.search).get("tab");
  if (requested !== null && isTenTabId(requested)) return requested;
  return DEFAULT_TEN_TAB;
}

/**
 * Die Tenant-Auswahl steht ebenfalls im URL-State (Owner-Auftrag): ein
 * geteilter Link auf Tenant 360° muss denselben Tenant oeffnen.
 */
function initialTenantFromQuery(): string | null {
  if (typeof window === "undefined") return null;
  const requested = new URLSearchParams(window.location.search).get("tenant");
  return requested !== null && requested.length > 0 ? requested : null;
}

function writeQuery(tab: TenTabId, tenantId: string | null): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  url.searchParams.set("tab", tab);
  if (tenantId === null) url.searchParams.delete("tenant");
  else url.searchParams.set("tenant", tenantId);
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}

/* ------------------------------------------------------------------ head */

/*
 * A-GTCC-POLSTERUNG-24PX1 — kein eigenes Polster. Den Rahmen der Ansicht setzt
 * die Shell (`GtccShell.tsx`: `18px 22px 30px`, identisch zur Referenz
 * `GTCC_REFERENCE_2026-09-01.html:62`). Ein `padding` hier polsterte ein
 * zweites Mal und verschob die Ansicht gemessen um 24 px nach innen.
 */
const VIEW_STYLE: CSSProperties = {
  display: "grid",
  gap: "var(--gt-sys-space-4)",
};

const HEAD_STYLE: CSSProperties = {
  margin: 0,
  display: "flex",
  alignItems: "baseline",
  gap: "var(--gt-sys-space-3)",
  flexWrap: "wrap",
};

/*
 * C-GTCC-SHELL-1ZU1 — Seitentitel = `h1` der Referenz
 * (GTCC_REFERENCE_2026-09-01.html:67): Sora 19 px/700 in `--tx`. Die Werte
 * kommen aus dem Token-Satz, damit jede Deck-View denselben Titel traegt;
 * vorher hat jede View ihre eigene Typo-Stufe gesetzt (15/19/20/24 px).
 */
const HEAD_TITLE_STYLE: CSSProperties = {
  margin: 0,
  fontFamily: "var(--gt-cmp-gtcc-view-title-family)",
  fontSize: "var(--gt-cmp-gtcc-view-title-size)",
  fontWeight: "var(--gt-cmp-gtcc-view-title-weight)",
  color: "var(--tx)",
};

/**
 * `.fresh` of the reference head. The age is never computed from the local
 * clock. None of the tenant endpoints carries a generation timestamp except
 * the launch status (`computedAt`, `TenantLaunchStatusCard.tsx:47`), so that
 * is the measurement time — and without a selected tenant there is nothing
 * measured at all, which reads "nicht messbar" rather than a number (Owner
 * decisions P9/P10).
 */
function TenFreshBadge(props: { tenantId: string }): ReactNode {
  const { tenantId } = props;
  const launch = useQuery({
    queryKey: ["gtcc", "ten", "view", "launch", tenantId],
    queryFn: ({ signal }) => fetchTenLaunchStatus(tenantId, signal),
    ...TEN_QUERY_OPTIONS,
  });
  const data = launch.data;
  if (data === undefined || !data.ok) return <NotMeasurable testId="gtcc-ten-fresh" />;
  const computedAt = data.data.computedAt;
  if (typeof computedAt !== "string" || computedAt.length === 0) {
    return <NotMeasurable testId="gtcc-ten-fresh" />;
  }
  return <FleetFreshBadge seconds={undefined} measuredAt={computedAt} testId="gtcc-ten-fresh" />;
}

/* ------------------------------------------------------------------ view */

export function TenView(): ReactNode {
  const { t, direction } = useLocale();
  const [activeTab, setActiveTab] = useState<TenTabId>(initialTabFromQuery);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(initialTenantFromQuery);
  const tenants = useQuery({
    queryKey: TEN_TENANTS_KEY,
    queryFn: ({ signal }) => fetchTenTenants(signal),
    ...TEN_QUERY_OPTIONS,
  });
  const tenantForFreshness =
    selectedTenantId ??
    (tenants.data !== undefined && tenants.data.ok ? (tenants.data.data[0]?.id ?? null) : null);

  // D-GTCC-FRAME1/D-GTCC-ACCEPT-SHELL1 — the Shell's breadcrumb reads the
  // active tab from `?tab=` (GtccShell.tsx) to show it as the 3rd segment;
  // without the query param a fresh load (no `?tab=` yet) leaves the
  // breadcrumb one level short even though a tab is in fact active. Writing
  // the resolved id once on mount keeps the URL truthful without changing
  // any rendered content (mirrors GtccCockpitView.tsx:654).
  useEffect(() => {
    writeQuery(activeTab, selectedTenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onTabChange = useCallback(
    (next: string) => {
      if (!isTenTabId(next)) return;
      setActiveTab(next);
      writeQuery(next, selectedTenantId);
    },
    [selectedTenantId],
  );

  const onOpenTab = useCallback(
    (next: TenTabId) => {
      setActiveTab(next);
      writeQuery(next, selectedTenantId);
    },
    [selectedTenantId],
  );

  /** "360° oeffnen →" and the member/invitation jumps. Navigation, no write. */
  const onOpenTenant = useCallback((tenantId: string, next: TenTabId) => {
    setSelectedTenantId(tenantId);
    setActiveTab(next);
    writeQuery(next, tenantId);
  }, []);

  const items: ReadonlyArray<TabItem> = TEN_TAB_IDS.map((id) => ({
    id,
    label: t(`admin.gtcc.ten.tab.${id}`),
    testId: `${TAB_ID_PREFIX}-tab-${id}`,
  }));

  const tabProps = { direction, selectedTenantId, onOpenTenant, onOpenTab } as const;

  return (
    <div data-testid="gtcc-view-ten" style={VIEW_STYLE}>
      <Stack gap={1}>
        {/*
         * Kopf und `.fresh` stehen im Referenz-Markup nebeneinander im selben
         * Flex-Kasten, nicht ineinander. Das ist hier auch die barrierefreie
         * Form: im Ueberschriftselement wuerde das Badge in den Accessible Name
         * der Ueberschrift wandern ("Tenants & Access nicht messbar"), und jede
         * Suche nach der Ueberschrift ginge ins Leere.
         */}
        <div style={HEAD_STYLE}>
          {/*
           * Eigene Schluessel statt `…ten.title` / `…ten.subtitle`: die alte
           * View traegt diese beiden Texte weiter, bis D-GTCC-SHELL1 gemergt
           * ist. Zwei Oberflaechen, zwei Texte — kein stiller Umtext einer
           * Seite, die noch jemand benutzt.
           */}
          <h2 style={HEAD_TITLE_STYLE}>{t("admin.gtcc.ten.head_title")}</h2>
          {/*
           * Ohne gewaehlten Tenant wird das Badge gar nicht erst montiert. Eine
           * per `enabled: false` stillgelegte Query bliebe im Cache fuer immer
           * `pending` — das ist kein gemessener Ladezustand, sondern ein
           * Zustand, der nie endet, und Q21 misst genau das.
           */}
          {tenantForFreshness === null ? (
            <NotMeasurable testId="gtcc-ten-fresh" />
          ) : (
            <TenFreshBadge tenantId={tenantForFreshness} />
          )}
        </div>
        <p style={SUB_STYLE} data-testid="gtcc-ten-sub">
          {t("admin.gtcc.ten.sub")}
        </p>
      </Stack>
      <div style={TAB_BAR_STYLE}>
        <Tabs
          items={items}
          value={activeTab}
          onChange={onTabChange}
          ariaLabel={t("admin.gtcc.ten.tabs_aria")}
          idPrefix={TAB_ID_PREFIX}
          direction={direction}
          variant="underline"
          dataTestId="gtcc-ten-tablist"
        />
      </div>
      <div
        role="tabpanel"
        id={`${TAB_ID_PREFIX}-panel-${activeTab}`}
        aria-labelledby={`${TAB_ID_PREFIX}-tab-${activeTab}`}
        data-testid={`gtcc-ten-panel-${activeTab}`}
      >
        {activeTab === "tenants" ? <TenantsTab {...tabProps} /> : null}
        {activeTab === "t360" ? <Tenant360Tab {...tabProps} /> : null}
        {activeTab === "provisioning" ? <ProvisioningTab {...tabProps} /> : null}
        {activeTab === "members" ? <MembersTab {...tabProps} /> : null}
        {activeTab === "invitations" ? <InvitationsTab {...tabProps} /> : null}
        {activeTab === "roles" ? <RolesTab {...tabProps} /> : null}
      </div>
    </div>
  );
}
